import { css } from 'styled-components'
import { bp, font } from 'lib/core/resources/helpers/styled'
import { BP } from 'resources/constants'
import { BREAKPOINT } from 'app/sofia/resources/constants'
import FacebookSvg from 'resources/svg/icons/facebook-square.svg'

export const heading1 = css`
  font: ${font('alt', 'normal', '26px/1.075')};

  @media (min-width: ${BP.md.min}) {
    font-size: 32px;
  }

  @media (min-width: ${BP.lg.min}) {
    font-size: 38px;
  }
`

export const heading2 = css`
  font: ${font('alt', 'normal', '22px/1.1')};

  @media (min-width: ${BP.md.min}) {
    font-size: 24px;
  }

  @media (min-width: ${BP.lg.min}) {
    font-size: 30px;
  }
`

export const buttonPrimary = css`
  min-width: unset;
  color: ${props => props.theme.palette.general.light};
  font: ${font('default', 'light', '17px/1')};
  text-align: center;
  border-radius: 3px;
  text-decoration: none;
  box-sizing: border-box;
  background: ${props => props.theme.palette.primary.alphaDark};
  border: 2px solid ${props => props.theme.palette.primary.alphaDark};
  transition: all 100ms ease, opacity 100ms ease;
  box-shadow: none;
  cursor: pointer;
  user-select: none;
  -webkit-user-drag: none;
  padding: 0 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => props.theme.palette.general.buttonSizeDefault};

  @media (min-width: ${BP.sm.min}) {
    display: inline-flex;
  }

  > span,
  > div {
    white-space: nowrap;
  }

  &:visited {
    background: ${props => props.theme.palette.primary.alphaDark};
    border-color: ${props => props.theme.palette.primary.alphaDark};
  }

  &:hover {
    background: ${props => props.theme.palette.general.strong};
    border-color: ${props => props.theme.palette.general.strong};
    color: ${props => props.theme.palette.general.light};
  }

  &:focus {
    outline: unset;
  }

  &:active {
    background: ${props => props.theme.palette.general.greyDark};
    border-color: ${props => props.theme.palette.general.greyDark};
    color: ${props => props.theme.palette.general.light};
  }

  &::-moz-focus-inner {
    padding: 0;
    border: 0;
  }

  &[disabled] {
    background: ${props => props.theme.palette.general.greyDark};
    border-color: ${props => props.theme.palette.general.greyDark};
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const buttonPrimaryOutline = css`
  ${buttonPrimary};
  background: none;
  border-color: ${props => props.theme.palette.primary.alphaDark};
  color: ${props => props.theme.palette.primary.alphaDark};

  &:hover,
  &:focus {
    color: ${props => props.theme.palette.general.alphaDark};
    background: ${props => props.theme.palette.primary.alphaDark};
    border-color: ${props => props.theme.palette.primary.alphaDark};
  }
`

export const buttonSecondary = css`
  ${buttonPrimary};
  color: ${props => props.theme.palette.general.alphaDark};
  background: ${props => props.theme.palette.general.strong};
  border-color: ${props => props.theme.palette.general.strong};

  &:hover,
  &:focus {
    color: ${props => props.theme.palette.general.alphaDark};
    background: ${props => props.theme.palette.primary.alphaDark};
    border-color: ${props => props.theme.palette.primary.alphaDark};
  }
`

export const buttonGreen = css`
  ${buttonPrimary};
  background: ${props => props.theme.palette.primary.beta};
  border-color: ${props => props.theme.palette.primary.beta};
  color: ${props => props.theme.palette.general.light};

  &:visited {
    background: ${props => props.theme.palette.primary.beta};
    border-color: ${props => props.theme.palette.primary.beta};
  }

  &:hover,
  &:focus {
    background: ${props => props.theme.palette.general.strong};
    border-color: ${props => props.theme.palette.general.strong};
  }
`

export const buttonSizeLg = css`
  min-height: ${props => props.theme.palette.general.buttonSizeLg};
  font-size: 22px;
`

export const buttonSizeSm = css`
  min-height: ${props => props.theme.palette.general.buttonSizeSm};
  font-size: 15px;
`

export const container = css`
  width: 85%;
  max-width: 1635px;
  margin: auto;
  padding: 0 40px;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINT.LG}) {
    max-width: 1088px;
  }

  ${bp('lg')`
    max-width: 1003px;
  `}

  ${bp('sm')`
    width: 100%;
    padding: 0 15px;
  `}
`

export const containerWide = css`
  max-width: 1720px;
  margin: auto;
  padding: 0 50px;

  @media (max-width: ${BREAKPOINT.LG}) {
    max-width: 1280px;
  }

  ${bp('lg')`
    max-width: 1120px;
  `}
  ${bp('sm')`
    padding: 0 15px;
  `}
`

export const link = css`
  color: ${props => props.theme.palette.general.pinkDark};

  &:hover,
  &:focus,
  &:active {
    text-decoration: underline;
    color: ${props => props.theme.palette.primary.alphaDark};
  }
`

export const facebookButton = css`
  ${buttonPrimary}
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.palette.general.blueDark};
  border-color: ${props => props.theme.palette.general.blueDark};

  &::before {
    content: '';
    margin-right: 10px;
    width: 20px;
    height: 22px;
    background: url(${FacebookSvg}) no-repeat center;
    background-size: 18px;
    filter: invert(100%) sepia(1%) saturate(7494%) hue-rotate(273deg) brightness(103%) contrast(97%);
    display: inline-block;
    margin-left: auto;
  }
`

export const input = css`
  border: 1px solid ${props => props.theme.palette.general.borderColor};
  border-radius: 3px;
  font-size: 16px;
  color: ${props => props.theme.palette.general.headerColor};
  text-align: center;
  max-width: 100%;
`

export const formInputCss = css`
  box-sizing: border-box;
  width: 100%;
  color: ${props => props.theme.palette.general.headerColor};
  padding: 10px 12px;
  height: ${props => props.theme.palette.general.buttonSizeDefault};
  text-align: left;
  border: 1px solid ${props => props.theme.palette.general.borderColor};
  border-radius: 3px;
  background: #fff;
  outline: none !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  filter: none;

  &:hover,
  &:focus {
    border-color: ${props => props.theme.palette.general.borderColorDark};
    box-shadow: none;
  }
`
